import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Drawer,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { changerMonMotDePasse, getUser, majMonProfil } from "../utilities/adminservice";
import { loaderOff, loaderOn, logout, openSnackbar } from "../redux/actions";
import store from "../redux/store";

// ─── Design tokens ────────────────────────────────────────────────────────────
/* Couleurs de la charte (src/theme/tribeTheme.ts), mêmes clés qu'avant. */
const C = {
  bg: "var(--c-bg)",
  card: "var(--c-surface)",
  border: "var(--c-border)",
  borderAccent: "#1F1E1B",
  accent: "#1F1E1B",
  accentLight: "#57534A",
  accentMuted: "rgba(31,30,27,0.07)",
  text: "#1F1E1B",
  textSub: "#57534A",
  textMuted: "#57534A",
  success: "#356A0F",
  warning: "#7C5600",
  error: "#A23A0D",
} as const;

// ─── Shared input style ────────────────────────────────────────────────────────
const inputSx = {
  "& .MuiOutlinedInput-root": {
    background: "rgba(31,30,27,0.03)",
    borderRadius: "8px",
    color: C.text,
    "& fieldset": { borderColor: C.border },
    "&:hover fieldset": { borderColor: C.borderAccent },
    "&.Mui-focused fieldset": { borderColor: C.accentLight },
  },
  "& .MuiInputLabel-root": { color: C.textMuted },
  "& .MuiInputLabel-root.Mui-focused": { color: C.accentLight },
};

// ─── Card wrapper ──────────────────────────────────────────────────────────────
const cardSx = {
  background: C.card,
  border: "1.5px solid #1F1E1B",
  borderRadius: "8px",
  p: 3,
  mb: 3,
};

export default function Profile() {
  // ── Core identity state ──────────────────────────────────────────────────────
  const [Id, setId] = useState<number | null>(null);
  const [FirstName, setFirstName] = useState("");
  const [LastName, setLastName] = useState("");
  const [Email, setEmail] = useState("");
  const [Domain, setDomin] = useState("");
  const [Role, setRole] = useState("");

  // ── Password drawer state ────────────────────────────────────────────────────
  const [pwDrawerOpen, setPwDrawerOpen] = useState(false);
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [pwError, setPwError] = useState("");

  // ── formvalues — used for domain validation ──────────────────────────────────
  const [formvalues, setFormvalues]: any = useState({
    first_name: { name: "", value: "", error: false },
    last_name: { name: "", value: "", error: false },
    email: { name: "", value: "", error: false },
    password: { name: "", value: "", error: false },
    confirm_password: { name: "", value: "", error: false },
    given_User_Name: { name: "", value: "", error: false },
    domain: { name: "", value: "", error: false },
    role: { name: "", value: "", error: false },
    id: { name: "", value: "", error: false },
  });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLoggedIn = useSelector((state: any) => state.auth.isLoggedIn);
  const isTokenExpired = useSelector((state: any) => state.auth.isTokenExpired);


  // ── Fetch profile ────────────────────────────────────────────────────────────
  const fetchToken = async () => {
    const jwtToken = sessionStorage.getItem("jwtToken");
    if (!jwtToken) {
      dispatch(openSnackbar("Session introuvable, veuillez vous reconnecter.", "red"));
      return;
    }
    dispatch(loaderOn());
    try {
      const response = await getUser();
      setId(response.id);
      setFirstName(response.first_name ?? "");
      setLastName(response.last_name ?? "");
      setEmail(response.email ?? "");
      setDomin(response.domain ?? "");
      // role fait autorité côté serveur ; role_type peut être nul (comptes anciens).
      setRole(response.role ?? response.role_type?.role_name ?? "");
    } catch (error) {
      dispatch(openSnackbar("Impossible de charger votre profil", "red"));
    } finally {
      dispatch(loaderOff());
    }
  };

  useEffect(() => {
    fetchToken();
  }, [0]);

  // ── Form validation (domain-centric) ─────────────────────────────────────────
  const validateForm = (formValues: any) => {
    let invalid = false;
    Object.keys(formValues).forEach((key) => {
      if (formValues[key].error || formValues[key].value === "") {
        invalid = true;
      }
    });
    return invalid;
  };

  // ── handleEditUser ────────────────────────────────────────────────────────────
  const handleEditUser = async () => {
    if (Id === null || formvalues.domain.error) return;
    if (!FirstName.trim()) {
      dispatch(openSnackbar("Le prénom est requis", "red"));
      return;
    }
    dispatch(loaderOn());
    try {
      // Sans rôle : personne ne change son propre rôle, le serveur l'ignore.
      await majMonProfil(Id, { first_name: FirstName.trim(), last_name: LastName.trim(), domain: Domain.trim() });
      dispatch(openSnackbar("Profil mis à jour", "green"));
    } catch (error) {
      dispatch(openSnackbar("Erreur lors de la mise à jour du profil", "red"));
    } finally {
      dispatch(loaderOff());
    }
  };

  // ── Password validation ───────────────────────────────────────────────────────
  const validatePassword = (): string => {
    if (!oldPw) return "Saisissez votre mot de passe actuel.";
    if (newPw.length < 8) return "Le mot de passe doit contenir au moins 8 caractères.";
    if (!/[A-Z]/.test(newPw)) return "Le mot de passe doit contenir au moins 1 majuscule.";
    if (!/[0-9]/.test(newPw)) return "Le mot de passe doit contenir au moins 1 chiffre.";
    if (newPw !== confirmPw) return "Les mots de passe ne correspondent pas.";
    return "";
  };

  const handleChangePassword = async () => {
    const err = validatePassword();
    if (err) {
      setPwError(err);
      return;
    }
    if (Id === null) return;
    setPwError("");
    dispatch(loaderOn());
    try {
      // L'ancien mot de passe est vérifié par le serveur ; rien d'autre ne change.
      await changerMonMotDePasse(Id, oldPw, newPw);
      dispatch(openSnackbar("Mot de passe modifié", "green"));
      setPwDrawerOpen(false);
      setOldPw("");
      setNewPw("");
      setConfirmPw("");
    } catch (error: any) {
      const d = error?.response?.data;
      setPwError(typeof d === "string" && d.trim() ? d : "Erreur lors du changement de mot de passe.");
    } finally {
      dispatch(loaderOff());
    }
  };

  // ── Avatar initials ───────────────────────────────────────────────────────────
  const initials =
    (FirstName ? FirstName[0].toUpperCase() : "") +
    (LastName ? LastName[0].toUpperCase() : "");

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: C.bg,
        p: { xs: 2, md: 4 },
        color: C.text,
      }}
    >
      {/* ── Page header ─────────────────────────────────────────────────────── */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: C.text, mb: 0.5 }}>
          Mon Profil
        </Typography>
        <Typography sx={{ color: C.textSub, fontSize: "0.95rem" }}>
          Gérez vos informations personnelles
        </Typography>
      </Box>

      {/* ════════════════════════════════════════════════════════════════════════
          SECTION 1 — Identité
      ════════════════════════════════════════════════════════════════════════ */}
      <Box sx={cardSx}>
        {/* Avatar + title row */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 3, mb: 4 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              background: C.accent,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              boxShadow: "none",
            }}
          >
            <Typography sx={{ fontSize: "1.75rem", fontWeight: 700, color: "#fff" }}>
              {initials || "?"}
            </Typography>
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: C.text }}>
              {FirstName} {LastName}
            </Typography>
            <Typography sx={{ color: C.textMuted, fontSize: "0.85rem" }}>
              {Email}
            </Typography>
          </Box>
        </Box>

        {/* Form grid */}
        <Grid container spacing={3}>
          {/* Prénom */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Prénom"
              variant="outlined"
              fullWidth
              required
              value={FirstName}
              onChange={(e) => setFirstName(e.target.value)}
              sx={inputSx}
            />
          </Grid>

          {/* Nom */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Nom"
              variant="outlined"
              fullWidth
              value={LastName}
              onChange={(e) => setLastName(e.target.value)}
              sx={inputSx}
            />
          </Grid>

          {/* Email — read-only */}
          <Grid item xs={12} sm={6}>
            <TextField
              label={
                <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  Email
                  <Chip
                    label="Non modifiable"
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: "0.65rem",
                      background: "rgba(124,86,0,0.15)",
                      color: C.warning,
                      border: `1px solid rgba(124,86,0,0.3)`,
                    }}
                  />
                </Box>
              }
              variant="outlined"
              fullWidth
              value={Email}
              InputProps={{ readOnly: true }}
              sx={inputSx}
            />
          </Grid>

          {/* Domaine */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Domaine"
              variant="outlined"
              fullWidth
              value={Domain}
              error={formvalues.domain.error}
              helperText={formvalues.domain.error ? "Format invalide (ex: example.com)" : ""}
              onChange={(e) => {
                const value = e.target.value;
                const domainRegex = /^[a-z0-9.-]+\.[a-z]{2,}$/;
                setFormvalues((prev: any) => ({
                  ...prev,
                  domain: {
                    ...prev.domain,
                    error: value ? !domainRegex.test(value) : false,
                  },
                }));
                setDomin(value);
              }}
              sx={{
                ...inputSx,
                "& .MuiFormHelperText-root": { color: C.error },
              }}
            />
          </Grid>

          {/* Rôle — read-only */}
          <Grid item xs={12} sm={6}>
            <TextField
              label={
                <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  Rôle
                  <Chip
                    label={Role || "—"}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: "0.65rem",
                      background: C.accentMuted,
                      color: C.accentLight,
                      border: `1px solid ${C.borderAccent}`,
                    }}
                  />
                </Box>
              }
              variant="outlined"
              fullWidth
              value={Role}
              InputProps={{ readOnly: true }}
              sx={inputSx}
            />
          </Grid>
        </Grid>

        {/* Update button */}
        <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            onClick={handleEditUser}
            sx={{
              background: C.accent,
              color: "#fff",
              fontWeight: 600,
              borderRadius: "8px",
              px: 4,
              py: 1.2,
              textTransform: "none",
              boxShadow: "none",
              "&:hover": {
                background: "#1F1E1B",
                boxShadow: "none",
              },
            }}
          >
            Mettre à jour
          </Button>
        </Box>
      </Box>

      {/* ════════════════════════════════════════════════════════════════════════
          SECTION 2 — Sécurité
      ════════════════════════════════════════════════════════════════════════ */}
      <Box sx={cardSx}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: C.text, mb: 0.5 }}>
              Sécurité du compte
            </Typography>
            <Typography sx={{ color: C.textMuted, fontSize: "0.85rem" }}>
              Mettez à jour votre mot de passe régulièrement pour protéger votre compte.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            onClick={() => setPwDrawerOpen(true)}
            sx={{
              borderColor: C.borderAccent,
              color: C.accentLight,
              borderRadius: "8px",
              px: 3,
              py: 1,
              textTransform: "none",
              fontWeight: 600,
              "&:hover": {
                borderColor: C.accentLight,
                background: C.accentMuted,
              },
            }}
          >
            Changer le mot de passe
          </Button>
        </Box>
      </Box>

      {/* ── Password Drawer ──────────────────────────────────────────────────── */}
      <Drawer
        anchor="right"
        open={pwDrawerOpen}
        onClose={() => {
          setPwDrawerOpen(false);
          setPwError("");
        }}
        PaperProps={{
          sx: {
            width: 400,
            background: C.card,
            borderLeft: `1px solid ${C.border}`,
            p: 3,
            color: C.text,
          },
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, color: C.text }}>
          Changer le mot de passe
        </Typography>
        <Typography sx={{ color: C.textMuted, fontSize: "0.85rem", mb: 3 }}>
          Renseignez les champs ci-dessous pour mettre à jour votre mot de passe.
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          <TextField
            label="Ancien mot de passe"
            type="password"
            variant="outlined"
            fullWidth
            value={oldPw}
            onChange={(e) => setOldPw(e.target.value)}
            sx={inputSx}
          />
          <TextField
            label="Nouveau mot de passe"
            type="password"
            variant="outlined"
            fullWidth
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
            helperText="8 caractères minimum, dont une majuscule et un chiffre"
            sx={{
              ...inputSx,
              "& .MuiFormHelperText-root": { color: C.textMuted },
            }}
          />
          <TextField
            label="Confirmation"
            type="password"
            variant="outlined"
            fullWidth
            value={confirmPw}
            onChange={(e) => setConfirmPw(e.target.value)}
            sx={inputSx}
          />

          {pwError && (
            <Typography sx={{ color: C.error, fontSize: "0.85rem" }}>
              {pwError}
            </Typography>
          )}

          <Button
            variant="contained"
            fullWidth
            onClick={handleChangePassword}
            sx={{
              mt: 1,
              background: C.accent,
              color: "#fff",
              fontWeight: 600,
              borderRadius: "8px",
              py: 1.3,
              textTransform: "none",
              boxShadow: "none",
              "&:hover": {
                background: "#1F1E1B",
              },
            }}
          >
            Valider
          </Button>
        </Box>
      </Drawer>

    </Box>
  );
}
